import { Reorder, useDragControls } from 'framer-motion';
import { GripVertical, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CourseListItemAdmin } from '../../../../../lib/interfaces/courses';
import { UserJob } from '../../../../../lib/interfaces/user';
import { formatDateTimeDE } from '../../../../../lib/utils/date';
import CourseCover from '../../../components/course-cover/course-cover';
import { COURSE_COVER } from '../../../constants/course-cover';
import { USER_JOB_LABELS } from '../../../constants/user-job-labels';
import ReleaseBadge from '../release-badge/release-badge';

export interface CourseAdminCardProps {
  course: CourseListItemAdmin;
  onDelete: (course: CourseListItemAdmin) => void;
  /** When true the card becomes a sortable framer Reorder.Item and shows its drag handle. */
  sortable?: boolean;
  /** Fired before the list starts shifting — lets the page record where the card came from. */
  onDragStart?: (course: CourseListItemAdmin) => void;
  /** Fired once the drag settles — the page turns the card's new index into a move request. */
  onDragEnd?: (course: CourseListItemAdmin) => void;
}

const pluralize = (count: number, singular: string, plural: string) => `${count} ${count === 1 ? singular : plural}`;

const AudienceBadges = ({ allJobs, userJobs }: { allJobs: boolean; userJobs: UserJob[] }) => {
  if (allJobs) {
    return (
      <span
        className="text-[11px] font-semibold px-2 py-1 rounded-full text-white"
        style={{ background: 'linear-gradient(135deg, #5FD5DE 0%, #C0DE60 100%)' }}>
        Alle Rollen
      </span>
    );
  }
  if (userJobs.length === 0) {
    return <span className="text-[11px] text-[#888] italic">Keine Zielgruppe</span>;
  }
  return (
    <div className="flex gap-1 flex-wrap">
      {userJobs.map((job) => (
        <span key={job} className="text-[11px] font-medium px-2 py-1 rounded-full bg-[#F0F4FC] text-[#12282A]">
          {USER_JOB_LABELS[job]}
        </span>
      ))}
    </div>
  );
};

const CourseAdminCard = ({ course, onDelete, sortable = false, onDragStart, onDragEnd }: CourseAdminCardProps) => {
  // Drafts step back visually — dimmed content only, so the row still reads as intact, not broken.
  const contentOpacity = course.isReleased ? '' : 'opacity-60';

  const dragControls = useDragControls();

  const card = (
    <div
      className="bg-white rounded-2xl border border-[#E4E7EC] p-2 flex items-center gap-5"
      style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
      {sortable && (
        <button
          type="button"
          aria-label="Kurs verschieben"
          // The whole card is a link; without this the pointer-down starts a navigation instead of a drag.
          onPointerDown={(e) => {
            e.preventDefault();
            onDragStart?.(course);
            dragControls.start(e);
          }}
          onClick={(e) => e.preventDefault()}
          className="ml-1 text-[#888] hover:text-[#12282A] cursor-grab active:cursor-grabbing touch-none shrink-0">
          <GripVertical className="w-4 h-4" />
        </button>
      )}

      <div className={contentOpacity}>
        <CourseCover imageUrl={course.imageUrl} size="sm" alt={course.title} percentage={course.isReleased ? 100 : 0} />
      </div>

      <div className={`flex-1 min-w-0 flex flex-col gap-1 ${contentOpacity}`}>
        <h3 className="text-[16px] font-bold text-[#12282A] truncate">{course.title}</h3>
        <div className="flex items-center gap-1.5 text-[12px] text-[#888]">
          <span>{pluralize(course.moduleCount, 'Modul', 'Module')}</span>
          <span aria-hidden="true">·</span>
          <span>{pluralize(course.lessonCount, 'Lektion', 'Lektionen')}</span>
          <span aria-hidden="true">·</span>
          <span className="truncate">bearbeitet {formatDateTimeDE(course.updatedAt)}</span>
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        <ReleaseBadge isReleased={course.isReleased} />
        <AudienceBadges allJobs={course.audience.allJobs} userJobs={course.audience.userJobs} />
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(course);
          }}
          aria-label="Kurs löschen"
          className="w-9 h-9 rounded-full border border-[#E4E7EC] hover:bg-red-50 hover:border-red-300 flex items-center justify-center text-red-500">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const linkedCard = <Link to={`/learning-suite/admin/course/${course._id}`}>{card}</Link>;

  if (!sortable) return linkedCard;

  return (
    <Reorder.Item
      value={course}
      // Dragging is handle-only — a whole-card listener would swallow the click that opens the course.
      dragListener={false}
      dragControls={dragControls}
      onDragEnd={() => onDragEnd?.(course)}
      className="rounded-2xl">
      {linkedCard}
    </Reorder.Item>
  );
};

export default CourseAdminCard;

export const CourseAdminCardSkeleton = () => (
  <div
    className="bg-white rounded-2xl border border-[#E4E7EC] p-2 flex items-center gap-5 animate-pulse"
    style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.04)' }}>
    <div className="rounded-full bg-[#E4E7EC] shrink-0" style={{ width: COURSE_COVER.sm.size, height: COURSE_COVER.sm.size }} />
    <div className="flex-1 flex flex-col gap-2">
      <div className="h-4 w-48 bg-[#E4E7EC] rounded-full" />
      <div className="h-3 w-56 bg-[#E4E7EC] rounded-full" />
    </div>
    <div className="w-20 h-6 bg-[#E4E7EC] rounded-full shrink-0" />
    <div className="w-9 h-9 rounded-full bg-[#E4E7EC] shrink-0" />
  </div>
);
